import React, { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Button,
  Alert,
  Skeleton,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import { AutoAwesome, Refresh } from "@mui/icons-material";
import {
  summarizeStationActivity,
  type ActivityEvent,
  type OfficerActivity,
  type SummarizeActivityRequest,
  type StationMetrics,
} from "@/features/stationAdmin/api/stationActivityApi";
import type { LastLoginWindow } from "@/features/stationAdmin/hooks/useLastLoginWindow";
import { stationAdminKeys } from "@/features/stationAdmin/hooks/useStationActivity";

/** Events passed to the model for grounding are trimmed to keep the payload
 *  small and low-sensitivity (aggregated activity, not raw report content). */
const MAX_EVENTS_FOR_SUMMARY = 15;

interface AiSummaryCardProps {
  orgId: string;
  window: LastLoginWindow;
  metrics: StationMetrics;
  feed: ActivityEvent[];
  /** Top in-window report authors, forwarded so the briefing can name who
   *  drove the report activity. */
  officerActivity?: OfficerActivity[];
  stationName?: string;
  /** Gate: don't summarize until the underlying activity has loaded. */
  activityReady: boolean;
  /** When true, evidence activity failed to load, so metrics/feed omit all
   *  evidence. Suppress the briefing rather than assert zero evidence. */
  evidenceUnavailable?: boolean;
}

const NO_OFFICER_ACTIVITY: OfficerActivity[] = [];

/** Compact, stable signature of the inputs so the summary caches per window +
 *  metric set and only regenerates when the numbers actually change. */
function metricsSignature(m: StationMetrics): string {
  return [
    m.reportsCreated,
    m.reportsSigned,
    m.reportsCommitted,
    m.reportsInReview,
    m.evidenceUploaded,
    m.activeOfficers,
    m.reportsTouched,
    m.truncated,
  ].join("-");
}

export const AiSummaryCard: React.FC<AiSummaryCardProps> = ({
  orgId,
  window,
  metrics,
  feed,
  officerActivity = NO_OFFICER_ACTIVITY,
  stationName,
  activityReady,
  evidenceUnavailable = false,
}) => {
  const theme = useTheme();

  // The exact event slice sent to the model.
  const summaryEvents = useMemo(
    () => feed.slice(0, MAX_EVENTS_FOR_SUMMARY),
    [feed],
  );

  // Keep the query key and request body on the same object. Every value the
  // model sees therefore participates in cache identity: exact time bounds,
  // station name, metrics, event fields, and author attribution.
  const summaryRequest = useMemo<SummarizeActivityRequest>(
    () => ({
      window: {
        start: window.windowStart,
        end: window.windowEnd,
        hasPriorLogin: window.hasPriorLogin,
      },
      stationName,
      metrics,
      events: summaryEvents.map((e) => ({
        kind: e.kind,
        title: e.title,
        status: e.status,
        officerName: e.officerName,
        timestamp: e.timestamp,
      })),
      ...(officerActivity.length > 0 ? { officerActivity } : {}),
    }),
    [
      window.windowStart,
      window.windowEnd,
      window.hasPriorLogin,
      stationName,
      metrics,
      summaryEvents,
      officerActivity,
    ],
  );

  // A station with zero activity needs no model round-trip: render the empty
  // message immediately instead of a spinner that resolves to the same text.
  const isEmpty =
    activityReady &&
    metricsSignature(metrics) === "0-0-0-0-0-0-0-false" &&
    summaryEvents.length === 0;

  const query = useQuery({
    queryKey: [
      ...stationAdminKeys.all,
      "summary",
      orgId,
      summaryRequest,
    ],
    queryFn: ({ signal }) =>
      summarizeStationActivity(summaryRequest, signal),
    enabled: activityReady && orgId.length > 0 && !isEmpty && !evidenceUnavailable,
    // The summary is deterministic-ish for a given window; don't refetch on
    // focus, and keep it for the session so tab switches are instant.
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    retry: false,
    // When new activity re-keys the query, keep showing the previous briefing
    // (dimmed, spinner in the button) instead of collapsing to a skeleton for
    // the whole round trip.
    placeholderData: keepPreviousData,
  });

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: alpha(theme.palette.primary.main, 0.3),
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.04,
        )} 0%, ${alpha(theme.palette.primary.main, 0.0)} 60%)`,
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: "primary.main",
                width: 36,
                height: 36,
              }}
            >
              <AutoAwesome fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Activity Briefing
              </Typography>
              <Typography variant="caption" color="text.secondary">
                AI summary of your station&apos;s recent activity
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              size="small"
              label="AI-generated"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                fontWeight: 600,
              }}
            />
            <Button
              size="small"
              startIcon={
                query.isFetching ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <Refresh fontSize="small" />
                )
              }
              onClick={() => query.refetch()}
              // isEmpty/evidenceUnavailable must gate here too: refetch()
              // bypasses enabled:false, so without it a click would still
              // trigger a model call.
              disabled={
                query.isFetching ||
                !activityReady ||
                isEmpty ||
                evidenceUnavailable
              }
            >
              {query.isFetching ? "Regenerating…" : "Regenerate"}
            </Button>
          </Box>
        </Box>

        {/* role="status" (polite live region) announces the regenerated
            briefing to screen readers when it lands. */}
        <Box role="status">
          {evidenceUnavailable ? (
            <Typography
              variant="body1"
              sx={{ lineHeight: 1.65, color: "text.secondary" }}
            >
              Briefing paused while evidence activity can&apos;t be loaded — a
              summary now would understate evidence. Use Refresh to retry.
            </Typography>
          ) : isEmpty ? (
            <Typography
              variant="body1"
              sx={{ lineHeight: 1.65, color: "text.primary" }}
            >
              No notable station activity in this period.
            </Typography>
          ) : !activityReady || query.isPending ? (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Summarizing station activity…
                </Typography>
              </Box>
              <Skeleton variant="text" width="95%" />
              <Skeleton variant="text" width="88%" />
              <Skeleton variant="text" width="70%" />
            </Box>
          ) : query.isError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => query.refetch()}>
                  Retry
                </Button>
              }
            >
              Couldn&apos;t generate the activity summary right now.
            </Alert>
          ) : (
            <Typography
              variant="body1"
              sx={{
                whiteSpace: "pre-wrap",
                lineHeight: 1.65,
                color: "text.primary",
                // Previous briefing shown via placeholderData while the next
                // one generates — dim it so the staleness is visible. 0.7 (not
                // lower) keeps body text above WCAG AA 4.5:1 in the light
                // theme, where text.primary is only rgba(0,0,0,0.87).
                opacity: query.isFetching ? 0.7 : 1,
                transition: "opacity 150ms",
              }}
            >
              {query.data?.summary?.trim() ||
                "No notable station activity in this period."}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
